import React, { useEffect, useState } from 'react';

import { c } from 'ttag';

import { Button, OtpInput, useLoading } from '@freelancelabs/components';

const CODE_LENGTH = 6;
const CODE_VALIDITY_SECONDS = 5 * 60;
const RESEND_DELAY_SECONDS = 30;

interface Props {
    email: string;
    onSubmit: (code: string) => Promise<void>;
    onResend: () => Promise<void>;
    onCancel: () => void;
}

const maskEmail = (email: string) => {
    const [user = '', domain = ''] = email.split('@');
    return `${user.slice(0, 1)}••••@${domain}`;
};

const formatCountdown = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const EmailOtpForm = ({ email, onSubmit, onResend, onCancel }: Props) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, withSubmitting] = useLoading();
    const [validity, setValidity] = useState(CODE_VALIDITY_SECONDS);
    const [resendIn, setResendIn] = useState(RESEND_DELAY_SECONDS);

    useEffect(() => {
        const timer = setInterval(() => {
            setValidity((s) => Math.max(0, s - 1));
            setResendIn((s) => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleSubmit = () => {
        if (submitting || code.length < CODE_LENGTH) {
            return;
        }
        setError('');
        withSubmitting(onSubmit(code)).catch(() => {
            setError(c('Mfa error').t`Invalid or expired code.`);
            setCode('');
        });
    };

    const handleResend = () => {
        setError('');
        setCode('');
        withSubmitting(onResend()).then(() => {
            setValidity(CODE_VALIDITY_SECONDS);
            setResendIn(RESEND_DELAY_SECONDS);
        });
    };

    const maskedEmail = <b key="masked-email">{maskEmail(email)}</b>;
    const countdown = formatCountdown(validity);

    return (
        <form
            name="email-otp-form"
            onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
            }}
            method="post"
        >
            <p className="mfa-sous">{c('Mfa help').jt`Enter the 6-digit code sent to ${maskedEmail}.`}</p>
            <OtpInput
                value={code}
                onValue={setCode}
                length={CODE_LENGTH}
                disabled={submitting || validity === 0}
                error={Boolean(error)}
                ariaLabel={c('Mfa label').t`6-digit verification code`}
            />
            <p className="mfa-statut" role="status">
                {validity === 0 ? c('Mfa error').t`This code is no longer valid. Send a new one.` : error}
            </p>
            <div className="mfa-meta">
                <span className={validity === 0 ? 'mfa-validite err' : 'mfa-validite'}>
                    {validity === 0
                        ? c('Mfa validity').t`Code expired`
                        : c('Mfa validity').t`Code valid for ${countdown}`}
                </span>
                <button
                    type="button"
                    className="mfa-renvoyer"
                    disabled={resendIn > 0 || submitting}
                    onClick={handleResend}
                >
                    {resendIn > 0 ? c('Mfa resend').t`Resend in ${resendIn} s` : c('Mfa resend').t`Resend the code`}
                </button>
            </div>
            <div className="mfa-actions">
                <Button category="ghost" type="button" onClick={onCancel} disabled={submitting}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button type="submit" loading={submitting} disabled={code.length < CODE_LENGTH || validity === 0}>
                    {c('Action').t`Verify`}
                </Button>
            </div>
        </form>
    );
};

export default EmailOtpForm;
